import { authedFetch, UnauthorizedError } from '@apis/auth';

/**
 * Worker Timesheet domain types — FE mirrors of the BFF response shapes
 * for `GET /api/sites/:siteId/analytics/{tags,timesheet}`. The BFF
 * resolves display fields server-side, preserves request tag order in
 * `byTag`, and emits ISO strings with the project tz offset baked in
 * (`arrivalIso` / `exitIso`), so consumers never re-convert.
 *
 * These intentionally duplicate the backend's `core/analytics/ports.ts`
 * shape: the wire response is the FE contract, and `core/*` types are
 * not importable from the frontend.
 */

export interface IAnalyticsTag {
  tagId: string;
  fullName: string;
  /**
   * Short numeric / alias upstream stamps on the badge — the Worker
   * Timesheet header renders this as the prominent big-number ID
   * (alongside the worker's full name). Nullable because not every
   * upstream variant (legacy / utility tags) carries it.
   */
  tagNick: string | null;
  idNumber: string | null;
  kind: 'worker' | 'manager';
  profession: {
    tradeId: string;
    /**
     * Friendly display label (e.g. "Stitch Works"). Sourced from
     * upstream's `profession.name` / `.nick` / `.tradeName`, whichever
     * is populated. Null when upstream only carries an opaque tradeId.
     */
    tradeName: string | null;
    tradeGroup: string | null;
    contractor: string | null;
    specialty: string | null;
    /**
     * Upstream-provided fill colour for the profession chip (Trusstor
     * stamps a brand colour on the profession row). `null` when omitted —
     * the picker falls back to a hash-derived palette so each profession
     * still has a stable colour.
     */
    backgroundColor: string | null;
  } | null;
}

export interface IWorkerTimesheetEntry {
  /** YYYY-MM-DD in project tz. */
  dateIso: string;
  /** Full ISO with project-tz offset (e.g. `+09:00`). */
  arrivalIso: string;
  /** Same shape as `arrivalIso`; null for an open shift (no checkout yet). */
  exitIso: string | null;
}

export interface IWorkerTimesheetForTag {
  tag: IAnalyticsTag;
  entries: IWorkerTimesheetEntry[];
  /** YYYY-MM-DD strings in project tz, within the report window. */
  absenceDates: string[];
}

export interface IWorkerTimesheetReport {
  range: { fromIso: string; toIso: string };
  /**
   * Keyed by tagId. Insertion order matches the request's `tagIds` array,
   * so consumers can iterate `Object.keys` to preserve user-driven order
   * without re-sorting.
   */
  byTag: Record<string, IWorkerTimesheetForTag>;
}

export interface IFetchWorkerTimesheetParams {
  tagIds: string[];
  /** YYYY-MM-DD in project tz. */
  dateFrom: string;
  /** YYYY-MM-DD in project tz. */
  dateTo: string;
}

async function handle(res: Response): Promise<never> {
  const body = await res.json().catch(() => null);
  // 401 reaches us only after authedFetch's refresh-and-retry has already
  // failed and dispatched `dorca:auth-expired`; we just surface the error
  // type so React Query lands in an error state until the redirect fires.
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`request failed: ${res.status} ${body?.error?.message ?? ''}`);
}

/**
 * Fetches the analytics tag catalog used by the Worker Timesheet picker.
 *
 * The endpoint returns a `{ tags: [...] }` envelope (distinct from the
 * legacy Daily Route `/sites/:siteId/tags` route which returns a bare
 * array); this helper unwraps so callers always see `IAnalyticsTag[]`.
 */
export async function fetchTags(
  siteId: string,
  signal?: AbortSignal,
): Promise<IAnalyticsTag[]> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/analytics/tags`,
    { signal },
  );
  if (!res.ok) return handle(res);
  const body = (await res.json()) as { tags: IAnalyticsTag[] };
  return body.tags;
}

/**
 * Fetches the worker timesheet report for a set of tags within a date
 * range. `tagIds` is serialised as **repeated** query params
 * (`?tagIds=a&tagIds=b`) — Fastify's default parser folds the repeats
 * into an array, which is what the backend Zod schema expects. Using
 * `URLSearchParams.append` (not `.set`) preserves the repetition.
 */
export async function fetchWorkerTimesheet(
  siteId: string,
  params: IFetchWorkerTimesheetParams,
  signal?: AbortSignal,
): Promise<IWorkerTimesheetReport> {
  const qs = new URLSearchParams();
  for (const tagId of params.tagIds) {
    qs.append('tagIds', tagId);
  }
  qs.append('dateFrom', params.dateFrom);
  qs.append('dateTo', params.dateTo);
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/analytics/timesheet?${qs.toString()}`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return (await res.json()) as IWorkerTimesheetReport;
}
